// SIM-2729 slice 2: the "Last run" cell on a transform row.
//
// Mirrors the table column's affordances deliberately — same dot vocabulary,
// same hover-and-click panel — so the two config surfaces read as one language.
// Every number comes from `QueryRun`, the authoritative execution record.

import type { RunDot, TransformRunStatus } from "@Simplicity-Education/core";
import { Popover, PopoverAnchor, PopoverContent } from "@simplicity/ui";
import { useState } from "react";
import { formatDurationMinutes, formatRelativeTime } from "../../lib/formatRelativeTime";

const DOT_CLASS: Record<RunDot, string> = {
  fresh: "bg-success",
  running: "bg-success animate-pulse",
  late: "bg-warning",
  failed: "bg-destructive",
  never: "bg-muted-foreground",
  unscheduled: "bg-muted-foreground",
  // SIM-2913. `paused` and `unknown` are deliberately NOT alarm colours: one is
  // off by design, the other means we cannot tell — neither is a defect, and
  // colouring them amber trains people to ignore amber.
  paused: "bg-muted-foreground",
  unknown: "bg-muted-foreground",
  // Declared for exhaustiveness; not emitted until the scope stamp gives a
  // count something to be judged against (SIM-2914).
  empty: "bg-warning",
  suspect: "bg-warning",
};

const DOT_LABEL: Record<RunDot, string> = {
  fresh: "Ran on time",
  running: "Run in progress",
  late: "Overdue",
  failed: "Last run failed",
  never: "Never run",
  unscheduled: "Nothing targets this table",
  // SIM-2913: a disabled definition now reports `paused`, so `unscheduled` no
  // longer has to mean both "switched off" and "nothing targets this".
  paused: "Disabled",
  unknown: "Cannot be determined",
  empty: "Wrote no rows",
  suspect: "Ran on time, data looks wrong",
};

const CADENCE_LABEL: Record<string, string> = {
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  adhoc: "Manual run",
  other: "Other",
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-foreground">{children}</dd>
    </div>
  );
}

/**
 * States whose sentence needs no numbers. A table rather than switch arms so
 * `verdictLine` stays under the complexity cap as the vocabulary grows.
 */
const STATIC_VERDICT: Partial<Record<TransformRunStatus["dot"], string>> = {
  paused: "This transform is disabled, so nothing is expected to run.",
  unscheduled: "Nothing in the config targets this table, so nothing is due.",
  unknown: "Not enough recent history to judge this one either way.",
  empty: "The last run finished but wrote no rows.",
  suspect: "Ran on time, but what it wrote looks wrong.",
  failed: "The most recent run failed.",
  running: "A run is in progress and on time.",
};

function verdictLine(run: TransformRunStatus, nowMs: number, lookbackDays: number): string {
  const fixed = STATIC_VERDICT[run.dot];
  if (fixed) return fixed;

  if (run.dot === "never") return `No completed run in the last ${lookbackDays} days.`;

  if (run.dot === "late") {
    const within =
      run.windowMinutes == null ? "its cadence" : formatDurationMinutes(run.windowMinutes);
    return run.overdueByMinutes != null
      ? `Overdue by ${formatDurationMinutes(run.overdueByMinutes)} — usually runs within ${within}.`
      : "Overdue.";
  }
  return run.lastRunAt == null
    ? "Ran successfully."
    : `Ran ${formatRelativeTime(Date.parse(run.lastRunAt), nowMs)}.`;
}

function Detail({
  run,
  nowMs,
  lookbackDays,
}: {
  run: TransformRunStatus;
  nowMs: number;
  lookbackDays: number;
}) {
  return (
    <dl className="space-y-2 text-xs">
      <Row label="Writes">
        <span className="break-all font-mono text-[11px]">
          {run.outputDataset}.{run.outputTable}
        </span>
      </Row>

      <Row label="Last run">
        {run.lastRunAt == null ? (
          <span className="text-muted-foreground">never (last {lookbackDays}d)</span>
        ) : (
          <>
            {new Date(run.lastRunAt).toLocaleString()}
            {run.rowsAffected != null && (
              <span className="text-muted-foreground">
                {" "}
                — {run.rowsAffected.toLocaleString()} rows
              </span>
            )}
          </>
        )}
      </Row>

      {run.lastAttemptStatus === "failed" && (
        <Row label="Last attempt">
          <span className="text-destructive">failed</span>
          {run.lastAttemptAt && (
            <span className="text-muted-foreground">
              {" "}
              {formatRelativeTime(Date.parse(run.lastAttemptAt), nowMs)}
            </span>
          )}
          {run.lastError && (
            <p className="mt-1 break-words text-muted-foreground">{run.lastError}</p>
          )}
        </Row>
      )}

      {run.editedSinceLastRun && (
        <Row label="Saved">
          <span className="text-warning">
            {formatRelativeTime(Date.parse(run.editedAt), nowMs)} — after the last successful run
          </span>
          <p className="mt-1 text-muted-foreground">
            The definition was saved since it last produced its table. That may or may not have
            changed the SQL.
          </p>
        </Row>
      )}

      {run.perCadence.length > 0 ? (
        <Row label="By trigger">
          <div className="space-y-0.5">
            {run.perCadence.map((c) => (
              <div key={c.cadence} className="flex gap-2">
                <span className="w-20 shrink-0 text-muted-foreground">
                  {CADENCE_LABEL[c.cadence] ?? c.cadence}
                </span>
                <span>
                  {c.at == null ? "—" : formatRelativeTime(Date.parse(c.at), nowMs)}
                  {c.status === "failed" && <span className="text-destructive"> failed</span>}
                  {(c.status === "pending" || c.status === "in-progress") && c.at != null && (
                    <span className="text-warning"> — last attempt did not finish</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Row>
      ) : (
        <Row label="By trigger">
          <span className="text-muted-foreground">
            no scheduled run observed in the last {lookbackDays}d
          </span>
        </Row>
      )}

      <Row label="Verdict">{verdictLine(run, nowMs, lookbackDays)}</Row>
    </dl>
  );
}

export function TransformLastRunCell({
  run,
  loaded,
  nowMs,
  lookbackDays,
}: {
  run: TransformRunStatus | null;
  loaded: boolean;
  nowMs: number;
  lookbackDays: number;
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  if (!loaded) {
    return (
      <span className="text-xs text-muted-foreground" aria-label="Loading last run time">
        &hellip;
      </span>
    );
  }
  if (run == null) return <span className="text-xs text-muted-foreground">&mdash;</span>;

  const label = DOT_LABEL[run.dot];
  const text =
    run.lastRunAt == null ? "Never" : formatRelativeTime(Date.parse(run.lastRunAt), nowMs);

  function close() {
    setPinned(false);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        {/* Anchor + our own button, not PopoverTrigger: its click handler toggles
            the controlled `open` that hover already set, so clicking a
            hover-opened panel closed it. min-h-6 keeps the 24x24 target-size
            floor. */}
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={`${label} — show how this was determined`}
          className="-mx-1 inline-flex min-h-6 items-center gap-1.5 rounded-sm px-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(e) => {
            // The row navigates to the transform; the panel must not trigger it.
            e.stopPropagation();
            if (pinned) {
              close();
              return;
            }
            setPinned(true);
            setOpen(true);
          }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => {
            if (!pinned) setOpen(false);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            if (!pinned) setOpen(false);
          }}
        >
          <span
            aria-hidden="true"
            className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASS[run.dot]}`}
          />
          <span className={run.lastRunAt == null ? "italic text-muted-foreground" : ""}>
            {text}
          </span>
        </button>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[27rem]"
        onOpenAutoFocus={(e) => {
          if (!pinned) e.preventDefault();
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => {
          if (!pinned) setOpen(false);
        }}
        onEscapeKeyDown={close}
      >
        <Detail run={run} nowMs={nowMs} lookbackDays={lookbackDays} />
      </PopoverContent>
    </Popover>
  );
}
